import React, { useCallback } from 'react';
import { OverlayView } from '@react-google-maps/api';
import { StyledMapPinCard, StyledMapPinLabel, StyledMapPinLabelContainer, StyledMapPinLabelTip, StyledMapPinLabelTipShadow, styles } from './MapPin.styles';
import { createPortal } from 'react-dom';
import { StyleSystemProps } from '@withjoy/joykit';

export type MapPinProps = Merge<
  React.DOMAttributes<HTMLElement>,
  {
    showOnTop?: boolean;
    isActive?: boolean;
    label: string | React.ReactNode;
    position: { lat: number; lng: number };
    isCardVisible?: boolean;
  }
>;

export const MapPin: React.FC<MapPinProps> = ({ showOnTop, children, position, isActive, isCardVisible, label, ...props }) => {
  const isClickable = typeof props.onClick === 'function';

  return (
    <OverlayView mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} position={position}>
      <>
        {children || null}
        <StyledMapPinLabelContainer role="group" __css={styles.joyMapPinLabelContainer({ isActive, isClickable, showOnTop, isCardVisible })} {...props}>
          <StyledMapPinLabelTipShadow __css={styles.joyMapPinLabelTipShadow} />
          <StyledMapPinLabel __css={styles.joyMapPinLabel({ isActive, isClickable, isCardVisible })}>{label}</StyledMapPinLabel>
          <StyledMapPinLabelTip __css={styles.joyMapPinLabelTip({ isActive, isClickable, isCardVisible })} />
        </StyledMapPinLabelContainer>
      </>
    </OverlayView>
  );
};

export const MAP_CARDS_PORTAL_ID = 'hotel-selection-map-cards-portal';

export const MapPinWithCard: React.FC<
  MapPinProps & {
    /**
     * If true, the card will be rendered in a portal. This is useful when the card is not positioned on top of the marker.
     * 'portalContainer' must be provided when 'usePortal' is true.
     */
    usePortal?: boolean;
    portalContainer?: HTMLElement;
    cardStyleOverride?: StyleSystemProps;
  }
> = ({ children, usePortal, portalContainer, cardStyleOverride, ...props }) => {
  const renderCard = useCallback(() => {
    return (
      <StyledMapPinCard __css={styles.joyMapPinCard({ isCardVisible: props.isCardVisible, cardStyleOverride, usePortal })} isCardVisible={props.isCardVisible}>
        {children}
      </StyledMapPinCard>
    );
  }, [props.isCardVisible, cardStyleOverride, usePortal, children]);

  return <MapPin {...props}>{usePortal && portalContainer ? createPortal(renderCard(), portalContainer) : renderCard()}</MapPin>;
};
